import { useEffect, useRef } from 'react';
import axios from 'axios';
import { useQueryClient } from 'react-query';
import { useAuth0 } from '@auth0/auth0-react';
import useUser from './useUser';
import {
  AccessRole, CorporateRole, InviteStatus, User, UserType,
} from '../models/user';
import { getImpersonatedRole, getImpersonatedUserId } from '../utils/impersonation';

const apiUrl = process.env.REACT_APP_BASIX_API || '';

// Auth0 namespaced claim the backend reads the app user id from (ControllerBase.cs).
const USER_ID_CLAIM = 'https://basixbooks.com/userId';

// item 142: Pulse Group admin level. Void / archive / delete are reserved for Pulse
// Group staff (the vendor / super-admin level) — no tenant user, incl. the account
// Owner, gets them. A user qualifies via an explicit User.isSuperAdmin flag OR a Pulse
// Group login domain. (Frontend gate only for now; backend deny lands with item 34.)
// item 384 — super-admin narrowed to the FLAG + the two hardcoded identities (the
// whole-domain rule made every @pulseb2b.com employee a super-admin)
const HARDCODED_SUPER_ADMINS = ['chad@pulseb2b.com', 'daniel@pulseb2b.com'];
const isPulseEmail = (email?: string): boolean => HARDCODED_SUPER_ADMINS.includes((email ?? '').trim().toLowerCase());

// The logged-in user's own app record + a derived "financial access" flag. item 84/34:
// access now follows the user's CORPORATE ROLE — Inside/Outside Sales see money; the
// rest (Project Lead / Deployment / Development / Intern) get the project-only view
// (item 102). Owner/Accountant seats keep money access as a fallback. Backend
// enforcement (SecurityBL) is intentionally still allow-all and tuned with Chad — this
// is the UI-side gate only. Defaults to full access when nothing is set (still loading
// / no role) so no one is locked out.
const useCurrentUser = (): {
  user?: User;
  userType?: UserType;
  corporateRole?: CorporateRole | null;
  accessRole?: AccessRole;
  hasFinancialAccess: boolean;
  isAdmin: boolean;
  isAccountant: boolean;
  isTeam: boolean;
  isOutsideSales: boolean;
  // item 594 — operations/sales leader (full ops, no money visibility). Backend SecurityBL
  // is the real gate; this flag drives UI treatment. Derived from the real role OR the
  // impersonated role, exactly like isTeam / isOutsideSales.
  isExecutive: boolean;
  // item 843 — external Customer Builder: bound to one customer, gets the stripped portal only.
  // App.tsx branches on this to render the portal instead of the internal shell.
  isCustomerBuilder: boolean;
  boundCustomerId?: string | null;
  canModerate: boolean;
  isSuperAdmin: boolean;
  // item 499 — may approve extra pay (commissions + timesheets): super-admin, or an Admin
  // granted the flag. Mirrors SecurityBL.CanApproveExtraPayAsync (visibility only; backend enforces).
  canApproveExtraPay: boolean;
  // item 410 — impersonation opens to account Admins (backend mirrors this gate)
  canImpersonate: boolean;
  isLoading: boolean;
  // item 1014 (Chad: "it let him in but with access to nothing — that is scary") — an
  // authenticated session that CANNOT resolve to a Basix user (token has no binding claim,
  // or the record fetch is rejected) must fail CLOSED: App renders a hard-stop screen, not
  // an empty shell that looks like unauthorized entry.
  sessionBroken: boolean;
} => {
  const { user: auth0User } = useAuth0();
  const claimId = (auth0User?.[USER_ID_CLAIM] as string | undefined) ?? '';
  // item 1080 (Chad: "the impersonate feature for testing is not working") — USER
  // impersonation (item 851) maps the EFFECTIVE user server-side, but this hook fetched
  // the identity by the TOKEN's claim id — so every UI gate (nav chips, pay type, the IC
  // flag, role views) kept rendering as the super-admin, not the target. Fetch the
  // TARGET's record while impersonating; the derived flags below then just work.
  const impersonatedUserId = getImpersonatedUserId();
  // pass '' (not NIL) when there's no claim so useUser leaves the query disabled and
  // returns undefined rather than the blank getEmptyUserInfo() record
  const { data, status } = useUser({ id: impersonatedUserId || claimId });
  // item 1014 — authenticated but unbound (no claim: e.g. an unlinked Google account), or the
  // self-record fetch was rejected (deactivated / purged row). Both = broken session.
  const sessionBroken = (!!auth0User && !claimId) || (!!claimId && status === 'error');

  // item 198 — first login = invite accepted. When our own record loads still un-Accepted,
  // tell the backend once (idempotent self-service); refresh so the Users grid flips. Keyed
  // by id so it fires at most once per user per session, not on every re-render.
  const queryClient = useQueryClient();
  const acceptedRef = useRef<string | null>(null);
  useEffect(() => {
    if (impersonatedUserId) return; // item 1080 — never auto-accept the TARGET's invite
    if (!claimId || !data || data.inviteStatus === undefined) return;
    if (data.inviteStatus === InviteStatus.Accepted) return;
    if (acceptedRef.current === claimId) return;
    acceptedRef.current = claimId;
    axios.post(`${apiUrl}user/AcceptInvite`)
      .then(() => {
        queryClient.invalidateQueries(['user', claimId]);
        queryClient.invalidateQueries('users');
      })
      .catch(() => { acceptedRef.current = null; }); // allow a retry next mount on failure
  }, [claimId, data, queryClient, impersonatedUserId]);

  const userType: UserType | undefined = data?.userType;
  const corporateRole = data?.corporateRole;

  // item 34 — the Basix access role drives permissions. Mirrors the backend SecurityBL:
  //   isAdmin (administrative access: settings, users, commission approve) = Admin OR
  //   Accountant; canModerate (void/archive) = Admin OR Pulse super-admin; Team = limited.
  // Legacy fallback: a user whose accessRole is unset (or an Owner seat) is treated as
  // Admin so nobody is unexpectedly locked out before the role is populated.
  const accessRole = data?.accessRole;
  const roleIsAdmin = accessRole === AccessRole.Admin;
  const isAccountant = accessRole === AccessRole.Accountant;
  const isTeam = accessRole === AccessRole.Team;
  // item 272 — Outside Sales is an EXTERNAL rep self-service seat: they see ONLY their
  // Book of Business, their earnings, their commission report + payout request, Appearance.
  const isOutsideSales = accessRole === AccessRole.OutsideSales;
  // item 594 — operations/sales leader: full ops surfaces, zero money visibility.
  const isExecutive = accessRole === AccessRole.Executive;
  // item 843 — external Customer Builder (bound to one customer; portal-only).
  const isCustomerBuilder = accessRole === AccessRole.CustomerBuilder;
  const boundCustomerId = data?.boundCustomerId;
  // item 848 (SECURITY) — legacy fallback ONLY applies when a real user record loaded but has no
  // accessRole yet (a pre-item-34 Owner). A session whose record DOESN'T resolve (mis-linked /
  // unprovisioned login) must NOT default to admin — gate on `data` existing.
  const legacyAdmin = !!data && accessRole === undefined && (userType === undefined || userType === UserType.Owner);

  // item 185/102/205 — the money view is keyed on ACCESS ROLE now (Corporate Role was
  // removed from the user form): Team = project-only (no money); Admin + Accountant =
  // financial. Defaults to full access when the role is unresolved so nobody is locked out.
  // item 594 — Executive is a trusted OPS/sales leader but sees NO money: exclude it here so
  // the KPI money strip, financial bento, and money shortcuts all hide (mirrors SecurityBL,
  // which denies Executive every financial object).
  // item 850 — external reps (Outside Sales) + Customer Builders never see COMPANY financials
  // (KPI strip, live dashboard, financial alerts). Outside Sales still sees their OWN commissions
  // on My Book — that's a scoped view, not the company money layer gated here.
  const hasFinancialAccess = !isTeam && !isExecutive && !isOutsideSales && !isCustomerBuilder;

  const isAdmin = roleIsAdmin || isAccountant || legacyAdmin;

  // item 142: void / archive reserved for Admin or Pulse Group super-admin. Hard delete
  // stays super-admin only (isSuperAdmin). A user qualifies as super-admin via the
  // User.isSuperAdmin flag or a Pulse Group login domain.
  // item 1080 — while impersonating a USER, the Pulse-email boost is masked: the UI runs
  // with the TARGET's powers only (the backend already masks server-side).
  const isSuperAdmin = impersonatedUserId
    ? Boolean(data?.isSuperAdmin)
    : (Boolean(data?.isSuperAdmin) || isPulseEmail(auth0User?.email));
  const canModerate = roleIsAdmin || legacyAdmin || isSuperAdmin;
  // item 499 — super-admin (or legacy Owner) always; a plain Admin only with the granted flag
  const canApproveExtraPay = isSuperAdmin || legacyAdmin || (roleIsAdmin && Boolean(data?.canApproveExtraPay));

  // item 323 — role impersonation (super-admin only): every derived flag recomputes
  // from the impersonated role with super-admin powers OFF, mirroring SecurityBL's
  // masking so the UI hides exactly what the backend denies. Non-super-admins are
  // never masked (and the backend ignores their header anyway).
  // item 410 — account Admins may impersonate too (mirrors the Startup gate: super-admin
  // OR AccessRole.Admin; every impersonatable role is admin-tier or narrower)
  const canImpersonate = !impersonatedUserId && (isSuperAdmin || roleIsAdmin); // item 1080 — no nesting
  const impersonatedRole = getImpersonatedRole();
  if (impersonatedRole !== undefined && canImpersonate) {
    const iAdmin = impersonatedRole === AccessRole.Admin;
    const iAccountant = impersonatedRole === AccessRole.Accountant;
    const iTeam = impersonatedRole === AccessRole.Team;
    const iExecutive = impersonatedRole === AccessRole.Executive;
    return {
      user: data,
      userType,
      corporateRole,
      accessRole: impersonatedRole,
      // item 594 — impersonating Executive must ALSO drop the money view (Chad verifies the
      // Executive role via impersonation), mirroring the real-role branch above.
      hasFinancialAccess: !iTeam && !iExecutive,
      isAdmin: iAdmin || iAccountant,
      isAccountant: iAccountant,
      isTeam: iTeam,
      isOutsideSales: impersonatedRole === AccessRole.OutsideSales,
      isExecutive: iExecutive,
      isCustomerBuilder: impersonatedRole === AccessRole.CustomerBuilder,
      boundCustomerId: undefined, // impersonation never carries a customer binding
      canModerate: iAdmin,
      isSuperAdmin: false,
      // item 499 — an impersonated identity is never a granted approver (mirrors backend)
      canApproveExtraPay: false,
      canImpersonate: false,
      isLoading: Boolean(claimId) && status === 'loading',
      sessionBroken, // item 1014
    };
  }

  return {
    user: data,
    userType,
    corporateRole,
    accessRole,
    hasFinancialAccess,
    isAdmin,
    isAccountant,
    isTeam,
    isOutsideSales,
    isExecutive,
    isCustomerBuilder,
    boundCustomerId,
    canModerate,
    isSuperAdmin,
    canApproveExtraPay,
    canImpersonate,
    isLoading: Boolean(claimId) && status === 'loading',
    sessionBroken, // item 1014
  };
};

export default useCurrentUser;
